import { jsx } from "react/jsx-runtime";
import { createContext, useState, useRef, useCallback, useEffect, useMemo, useContext } from "react";
import { useIsMounted } from "../../../../lib/hooks/useIsMounted.js";
import { useIsVideoPaused } from "./isPaused.js";
const HIDE_DELAY_TIME = 2 * 1e3;
const IsUIVisibleContext = createContext(false);
IsUIVisibleContext.displayName = "IsUIVisibleContext";
function useIsUIVisible() {
  const isUIVisible = useContext(IsUIVisibleContext);
  if (isUIVisible == null) {
    throw new Error("useIsUIVisible called without a IsUIVisibleContext.Provider");
  }
  return isUIVisible;
}
const SetIsUIVisibleContext = createContext(
  /** @type {IsVisibleAPI | undefined} */
  void 0
);
SetIsUIVisibleContext.displayName = "SetIsUIVisibleContext";
function useSetIsUIVisible() {
  const setIsUIVisible = useContext(SetIsUIVisibleContext);
  if (!setIsUIVisible) {
    throw new Error("useSetIsUIVisible called without a SetIsUIVisibleContext.Provider");
  }
  return setIsUIVisible;
}
function IsUIVisibleProvider({ children, videoContainerRef }) {
  const isVideoPaused = useIsVideoPaused();
  const [isVisibleByInteraction, setIsVisibleByInteraction] = useState(true);
  const isMounted = useIsMounted();
  const hideTimeoutRef = useRef(
    /** @type {ReturnType<setTimeout> | undefined} */
    void 0
  );
  const hideUIImmediately = useCallback(() => {
    if (isMounted()) {
      setIsVisibleByInteraction(false);
    }
  }, [isMounted]);
  const cancelHideTimeout = useCallback(() => {
    window.clearTimeout(hideTimeoutRef.current);
    hideTimeoutRef.current = void 0;
  }, []);
  const resetHideTimeout = useCallback(() => {
    cancelHideTimeout();
    hideTimeoutRef.current = setTimeout(() => {
      hideUIImmediately();
    }, HIDE_DELAY_TIME);
  }, [cancelHideTimeout, hideUIImmediately]);
  const showUI = useCallback(() => {
    setIsVisibleByInteraction(true);
    resetHideTimeout();
  }, [resetHideTimeout]);
  useEffect(() => {
    if (isVideoPaused) {
      cancelHideTimeout();
    }
    if (!isVideoPaused) {
      resetHideTimeout();
    }
  }, [cancelHideTimeout, isVideoPaused, resetHideTimeout]);
  const container = videoContainerRef == null ? void 0 : videoContainerRef.current;
  useEffect(() => {
    container == null ? void 0 : container.addEventListener("mousemove", showUI);
    container == null ? void 0 : container.addEventListener("mouseleave", hideUIImmediately);
    return () => {
      container == null ? void 0 : container.removeEventListener("mousemove", showUI);
      container == null ? void 0 : container.removeEventListener("mouseleave", hideUIImmediately);
    };
  }, [showUI, hideUIImmediately, container]);
  const setIsVisibleAPI = useMemo(
    () => ({
      showUIThenHide: showUI,
      hideUIImmediately,
      resetHideTimeout,
      setIsVisibleByInteraction
    }),
    [showUI, hideUIImmediately, resetHideTimeout, setIsVisibleByInteraction]
  );
  const isVisible = isVideoPaused || isVisibleByInteraction;
  return /* @__PURE__ */jsx(SetIsUIVisibleContext.Provider, { value: setIsVisibleAPI, children: /* @__PURE__ */jsx(IsUIVisibleContext.Provider, { value: isVisible, children: typeof children === "function" ? children({ isVisible }) : children }) });
}
export {
  IsUIVisibleProvider,
  useIsUIVisible,
  useSetIsUIVisible };
//# sourceMappingURL=dist/components/VideoContextProvider/context/state/isUIVisible.js.map